import { Injectable, OnDestroy } from '@angular/core';
import { SwUpdate } from '@angular/service-worker';
import { interval, Subscription } from 'rxjs';
import { VersionDetectService } from './version-detect.service';
import { ConfirmPayload, SwalService } from '../swal.service';
import { VERSION_STORAGE_KEY, MAJOR_VERSION_STORAGE_KEY } from '../../constants';
@Injectable({
  providedIn: 'root',
})
export class AppUpdateService implements OnDestroy {
  private updateCheckSubscription = new Subscription();
  private reminderSubscription = new Subscription();
  private forceUpdateTimeout: any;
  private initializationTimeout: any;

  // Version tracking configuration

  private readonly UPDATE_DISMISSED_KEY = 'update-dismissed';
  private readonly SESSION_MANAGED_KEY = 'angular-version-managed';

  // Auth keys to check for existing user data
  private readonly AUTH_KEYS = ['access', 'featureAccess'];

  constructor(
    private swUpdate: SwUpdate,
    private versionDetectService: VersionDetectService,
    private swalService: SwalService
  ) {}

  public initializeUpdateService(): void {
    // Add delay to let PIQET Update Manager complete its initialization first
    this.initializationTimeout = setTimeout(() => {
      this.performDelayedInitialization();
    }, 1500); // 1.5 second delay
  }

  private performDelayedInitialization(): void {
    // Check if PIQET Update Manager has taken control
    if (this.isPiqetUpdateManagerActive()) {
      this.initializeLimitedMode();
      return;
    }

    // Proceed with full initialization
    this.initializeFullMode();
  }

  /**
   * Check if PIQET Update Manager is active and handling version control
   */
  private isPiqetUpdateManagerActive(): boolean {
    // Check if PIQET Update Manager exists
    if (!(window as any).piqetUpdateManager) {
      return false;
    }

    // Check if it has completed its version check
    const piqetManaged = sessionStorage.getItem('piqet-version-managed');
    if (piqetManaged) {
      return true;
    }

    // Check if versions are consistent (PIQET likely managed it)
    const metaVersion = this.getMetaVersion();
    const metaMajorVersion = this.getMetaMajorVersion();
    const storedVersion = localStorage.getItem(VERSION_STORAGE_KEY);
    const storedMajorVersion = localStorage.getItem(MAJOR_VERSION_STORAGE_KEY);

    if (
      metaVersion &&
      metaMajorVersion &&
      storedVersion === metaVersion &&
      storedMajorVersion === metaMajorVersion
    ) {
      return true;
    }

    return false;
  }

  /**
   * Initialize in limited mode - only handle service worker updates
   */
  private initializeLimitedMode(): void {
    // Mark that Angular service is running in limited mode
    sessionStorage.setItem(this.SESSION_MANAGED_KEY, 'limited');

    if (!this.swUpdate.isEnabled) {
      return;
    }

    // Only handle service worker updates, not version tracking
    this.setupServiceWorkerHandlers();
    this.setupPeriodicUpdateChecks();

    // Check for pending service worker updates only
    if (this.versionDetectService.isUpdatePending()) {
      this.showUpdateNotificationToUser();
    }
  }

  /**
   * Initialize in full mode - handle both version tracking and service worker
   */
  private initializeFullMode(): void {
    // Mark that Angular service is managing versions
    sessionStorage.setItem(this.SESSION_MANAGED_KEY, 'full');

    // Initialize version tracking from meta tags
    this.initializeVersionTracking();

    if (!this.swUpdate.isEnabled) {
      return;
    }

    this.setupServiceWorkerHandlers();
    this.setupPeriodicUpdateChecks();

    // Check if there's already a pending update on app initialization
    if (this.versionDetectService.isUpdatePending()) {
      this.showUpdateNotificationToUser();
    }
  }

  /**
   * Setup service worker event handlers
   */
  private setupServiceWorkerHandlers(): void {
    // Handle new version available
    this.swUpdate.available.subscribe(event => {
      this.handleNewVersionAvailable();
    });

    // Handle version activation
    this.swUpdate.activated.subscribe(event => {});
  }

  /**
   * Setup periodic update checks
   */
  private setupPeriodicUpdateChecks(): void {
    // Set up periodic update checks (every 30 seconds)
    this.updateCheckSubscription.add(
      interval(30000).subscribe(() => {
        this.swUpdate.checkForUpdate();
      })
    );
  }

  /**
   * Initialize version tracking from meta tags (only in full mode)
   */
  private initializeVersionTracking(): void {
    const currentVersion = this.getMetaVersion();
    const currentMajorVersion = this.getMetaMajorVersion();

    if (!currentVersion || !currentMajorVersion) {
      return;
    }

    // Check and update version flags with session protection
    this.checkAndUpdateVersionFlags(currentVersion, currentMajorVersion);
  }

  /**
   * Get app version from meta tag
   */
  private getMetaVersion(): string | null {
    const meta = document.querySelector('meta[name="app-version"]');
    return meta ? meta.getAttribute('content') : null;
  }

  /**
   * Get major version from meta tag
   */
  private getMetaMajorVersion(): string | null {
    const meta = document.querySelector('meta[name="major-version"]');
    return meta ? meta.getAttribute('content') : null;
  }

  /**
   * Check if user has authentication data stored
   */
  private hasAppData(): boolean {
    return this.AUTH_KEYS.some(
      key => localStorage.getItem(key) !== null || sessionStorage.getItem(key) !== null
    );
  }

  /**
   * Check current version against stored version with session protection
   */
  private checkAndUpdateVersionFlags(currentVersion: string, currentMajorVersion: string): void {
    // Session protection: Check if version update was already handled this session
    const sessionVersionCheck = sessionStorage.getItem('angular-version-check-completed');
    if (sessionVersionCheck === currentVersion) {
      // Ensure localStorage is set correctly
      this.setVersionInfo(currentVersion, currentMajorVersion);
      return;
    }

    const storedVersion = localStorage.getItem(VERSION_STORAGE_KEY);
    const storedMajorVersion = localStorage.getItem(MAJOR_VERSION_STORAGE_KEY);
    const hasExistingAppData = this.hasAppData();

    // Case 1: Users with complete version tracking - check for updates
    if (storedVersion && storedMajorVersion) {
      if (storedMajorVersion !== currentMajorVersion) {
        this.handleMajorVersionUpdate(currentVersion, currentMajorVersion);
        return;
      }

      if (storedVersion !== currentVersion) {
        this.setVersionInfoWithSession(currentVersion, currentMajorVersion);
        return;
      }

      this.setSessionVersionCheck(currentVersion);
      return;
    }

    // Case 2: Existing users who don't have version properties yet but have app data
    if (!storedVersion && !storedMajorVersion && hasExistingAppData) {
      this.handleMajorVersionUpdate(currentVersion, currentMajorVersion);
      return;
    }

    // Case 3: Brand new users - no version tracking and no app data
    if (!storedVersion && !storedMajorVersion && !hasExistingAppData) {
      this.setVersionInfoWithSession(currentVersion, currentMajorVersion);
      return;
    }

    // Case 4: Partial version info (edge case)
    if ((!storedVersion && storedMajorVersion) || (storedVersion && !storedMajorVersion)) {
      this.handleMajorVersionUpdate(currentVersion, currentMajorVersion);
      return;
    }
    this.handleMajorVersionUpdate(currentVersion, currentMajorVersion);
  }

  /**
   * Set current version information in localStorage and session
   */
  private setVersionInfo(version: string, majorVersion: string): void {
    localStorage.setItem(VERSION_STORAGE_KEY, version);
    localStorage.setItem(MAJOR_VERSION_STORAGE_KEY, majorVersion);
  }

  /**
   * Set version info and mark session as completed
   */
  private setVersionInfoWithSession(version: string, majorVersion: string): void {
    this.setVersionInfo(version, majorVersion);
    this.setSessionVersionCheck(version);
  }

  /**
   * Mark that version check was completed for this session
   */
  private setSessionVersionCheck(version: string): void {
    sessionStorage.setItem('angular-version-check-completed', version);
  }

  /**
   * Handle major version update - check for PIQET Update Manager first
   */
  private handleMajorVersionUpdate(currentVersion: string, currentMajorVersion: string): void {
    // Set the new version info first
    this.setVersionInfoWithSession(currentVersion, currentMajorVersion);

    // Check if PIQET Update Manager is available
    if ((window as any).piqetUpdateManager) {
      try {
        (window as any).piqetUpdateManager.performUpdate();
        return;
      } catch (error) {}
    } else {
    }

    // Fallback to existing service worker update logic
    this.versionDetectService.setUpdatePending(true);
    this.showUpdateNotificationToUser();
  }

  /**
   * Get current version from localStorage
   */
  public getCurrentStoredVersion(): string | null {
    return localStorage.getItem(VERSION_STORAGE_KEY);
  }

  /**
   * Get current major version from localStorage
   */
  public getCurrentStoredMajorVersion(): string | null {
    return localStorage.getItem(MAJOR_VERSION_STORAGE_KEY);
  }

  /**
   * Force a version check (useful for testing)
   */
  public forceVersionCheck(): void {
    // Clear session protection for testing
    sessionStorage.removeItem('angular-version-check-completed');

    const currentVersion = this.getMetaVersion();
    const currentMajorVersion = this.getMetaMajorVersion();

    if (currentVersion && currentMajorVersion) {
      this.checkAndUpdateVersionFlags(currentVersion, currentMajorVersion);
    } else {
    }
  }

  /**
   * Get debug information about current state
   */
  public getDebugInfo(): any {
    const currentVersion = this.getMetaVersion();
    const currentMajorVersion = this.getMetaMajorVersion();
    const storedVersion = localStorage.getItem(VERSION_STORAGE_KEY);
    const storedMajorVersion = localStorage.getItem(MAJOR_VERSION_STORAGE_KEY);
    const sessionCheck = sessionStorage.getItem('angular-version-check-completed');
    const sessionManaged = sessionStorage.getItem(this.SESSION_MANAGED_KEY);
    const piqetManaged = sessionStorage.getItem('piqet-version-managed');

    return {
      service: 'AppUpdateService',
      currentVersion,
      currentMajorVersion,
      storedVersion,
      storedMajorVersion,
      sessionCheck,
      sessionManaged,
      piqetManaged,
      hasAppData: this.hasAppData(),
      swEnabled: this.swUpdate.isEnabled,
      piqetManagerExists: !!(window as any).piqetUpdateManager,
    };
  }

  private handleNewVersionAvailable(): void {
    this.swUpdate
      .activateUpdate()
      .then(() => {
        if (this.versionDetectService.hasValidToken()) {
          // User is logged in - show notification but don't force reload
          this.versionDetectService.setUpdatePending(true);
          this.showUpdateNotificationToUser();
          this.setupForceUpdateTimeout();
        } else {
          // User is not logged in - force reload immediately
          window.location.reload();
        }
      })
      .catch(error => {});
  }

  showUpdateNotificationToUser(): void {
    const payload: ConfirmPayload = {
      title: 'Performance updates required',
      text: 'App performance updates required. You can update now or we will auto-update in 5 minutes.',
      iconHtml: '',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Update Now',
      cancelButtonText: 'Delay 5 Mins',
      customClass: 'Popupbackground eol-warning update-notification',
      iconColor: '#FFFFFF',
      enableAnimation: true,
      allowOutsideClick: false,
      reverseButtons: true,
      showConfirmButton: true,
    };
    this.swalService.confirmLegacy(payload).then(result => {
      if (result) {
        this.versionDetectService.applyUpdate();
      } else {
        this.setupForceUpdateTimeout();
      }
    });
  }

  private setupForceUpdateTimeout(): void {
    // Clear any existing timeout
    if (this.forceUpdateTimeout) {
      clearTimeout(this.forceUpdateTimeout);
    }

    this.forceUpdateTimeout = setTimeout(() => {
      this.showCountdownNotification();
      if (this.versionDetectService.isUpdatePending()) {
        this.showCountdownNotification();
      }
    }, 290000); // 4 minutes and 50 seconds
  }

  private showCountdownNotification(): void {
    let countdown = 10;

    // Show initial countdown notification
    const countdownPayload: ConfirmPayload = {
      title: 'Updating PIQET app',
      text: `This page will automatically reload in [${countdown}] seconds. Please save your changes.`,
      icon: 'warning',
      iconHtml: '',
      showCancelButton: false,
      showConfirmButton: false,
      allowOutsideClick: false,
      customClass: 'Popupbackground eol-warning auto-update-swal',
      iconColor: '#FFFFFF',
      enableAnimation: false,
    };

    // Show the countdown notification (it will resolve immediately since no buttons)
    this.swalService.confirmLegacy(countdownPayload);

    // Update countdown every second
    const countdownInterval = setInterval(() => {
      countdown--;

      if (countdown > 0) {
        const swalContainer = document.querySelector('.swal2-html-container span');
        if (swalContainer) {
          swalContainer.textContent = `This page will automatically reload in [${countdown}] seconds. Please save your changes.`;
        }
      } else {
        clearInterval(countdownInterval);
        this.versionDetectService.applyUpdate();
      }
    }, 1000);
  }

  public checkForUpdate(): void {
    if (this.swUpdate.isEnabled) {
      this.swUpdate
        .checkForUpdate()
        .then(updateFound => {
          if (updateFound) {
          } else {
          }
        })
        .catch(error => {
          console.error('AppUpdateService: Error checking for updates:', error);
        });
    }
  }

  // Method to manually apply update (can be called from UI)
  public applyUpdateNow(): void {
    this.versionDetectService.applyUpdate();
  }

  // Method to dismiss update notification (optional)
  public dismissUpdate(): void {
    this.versionDetectService.setUpdatePending(false);
    if (this.forceUpdateTimeout) {
      clearTimeout(this.forceUpdateTimeout);
    }
  }

  ngOnDestroy(): void {
    this.updateCheckSubscription.unsubscribe();
    this.reminderSubscription.unsubscribe();
    if (this.forceUpdateTimeout) {
      clearTimeout(this.forceUpdateTimeout);
    }
    if (this.initializationTimeout) {
      clearTimeout(this.initializationTimeout);
    }
  }
}
